import Router, { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";

type TransitionOptions = Parameters<typeof Router.push>[2];

export const useThrottledQueryState = (name = "q") => {
  // router can't be spreaded so isReady is properly updated
  const router = useRouter();

  const [state, setState] = useState<string | string[]>();

  useEffect(() => {
    if (router.isReady) {
      state === undefined && router.query[name] !== undefined && setState(router.query[name]);
    }
  }, [name, router.isReady, router.query, state]);

  const [act] = useDelayedAction((s: string | string[], opts?: TransitionOptions) => {
    const pathUrl = new URL("https://robe.cz" + router.pathname);
    const asUrl = new URL("https://robe.cz" + router.asPath);

    // empty string or empty array
    if (s.length === 0) {
      pathUrl.searchParams.delete(name);
      asUrl.searchParams.delete(name);
    } else if (Array.isArray(s)) {
      pathUrl.searchParams.delete(name);
      asUrl.searchParams.delete(name);
      s.forEach((v) => {
        if (v.length === 0) return;
        pathUrl.searchParams.append(name, v);
        asUrl.searchParams.append(name, v);
      });
    } else {
      pathUrl.searchParams.set(name, s);
      asUrl.searchParams.set(name, s);
    }

    router.push(pathUrl.pathname + pathUrl.search, asUrl.pathname + asUrl.search, { shallow: true, ...opts });
  }, 500);

  return [
    more(state),
    useCallback(
      (newQuery: string | string[], opts?: TransitionOptions) => {
        setState(newQuery);
        act(newQuery, opts);
      },
      [act]
    ),
  ] as const;
};

const useDelayedAction = <TArgs extends any[]>(action: (...args: TArgs) => unknown, delay = 200) => {
  const queued = useRef<[(...args: TArgs) => unknown, NodeJS.Timeout]>();

  const prevent = useCallback(() => {
    queued.current = undefined;
  }, []);

  const act = useCallback(
    (...args: TArgs) => {
      queued.current && clearTimeout(queued.current[1]);

      const timer = setTimeout(() => {
        queued.current![0]?.(...args);
        queued.current = undefined;
      }, delay);
      queued.current = [action, timer];
    },
    [action, delay]
  );

  return [act, prevent] as const;
};

const more = <T>(q?: T | T[]) => (!q ? [] : Array.isArray(q) ? q : [q]);
